import React, { useEffect, useState, useRef } from 'react';
import LoggedInLayout from '../../layout/LoggedInLayout';

import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  useMediaQuery,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Icons
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import SelfImprovementOutlinedIcon from '@mui/icons-material/SelfImprovement';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import AssessmentGuidelines from './AssessmentGuidelines';
import AssessmentStepRender from './AssessmentStepRender';
import { ArrowBack } from '@mui/icons-material';
import AssessmentPdf from './AssessmentPdf';
import httpcommon from '../../helper/httpcommon';
import { useNavigate } from 'react-router-dom';

// ---- Palette (aligned with the platform) --------------------------------
const C = {
  navy: '#292D5B',
  ink: '#3E4A66',
  muted: '#8A90A3',
  orange: '#F6A03A',
  orangeDark: '#E8912B',
  green: '#2E7D32',
  line: '#E7EAF1',
  surface: '#FFFFFF',
  bg: '#F5F6FA',
};

// Backend-driven steps
const initialSteps = [
  {
    id: 'marks_uploaded',
    label: 'Provide your Marks',
    short: 'Your Marks',
    icon: <DescriptionOutlinedIcon />,
    is_Completed: false,
  },

  {
    id: 'general_personality_question',
    label: 'Help Us Know You',
    short: 'About You',
    icon: <PsychologyOutlinedIcon />,
    is_Completed: false,
  },
  {
    id: 'aptitude_test',
    label: 'Take Aptitude Test',
    short: 'Aptitude',
    icon: <QuizOutlinedIcon />,
    is_Completed: false,
  },
  {
    id: 'self_introspection',
    label: 'Self Introspection',
    short: 'Introspection',
    icon: <SelfImprovementOutlinedIcon />,
    is_Completed: false,
  },
  {
    id: 'parent_feedback_taken',
    label: 'Parent/Teacher Feedback',
    short: 'Feedback',
    icon: <GroupOutlinedIcon />,
    is_Completed: false,
  },
];

const createInitialSteps = () => initialSteps.map((step) => ({ ...step }));


// Slim step connector: hairline that warms to orange / green as you progress.
const Connector = styled(StepConnector)(() => ({
  top: 18,
  left: 'calc(-50% + 24px)',
  right: 'calc(50% + 24px)',
  '& .MuiStepConnector-line': {
    borderTopWidth: 2,
    borderRadius: 2,
    borderColor: '#E7EAF1',
    transition: 'border-color .3s ease',
  },
  '&.Mui-active .MuiStepConnector-line': {
    borderColor: '#F6A03A',
  },
  '&.Mui-completed .MuiStepConnector-line': {
    borderColor: '#2E7D32',
  },
}));

// Compact circular step marker: icon → check as you complete each step.
const CompactStepIcon = ({ active, completed, glyph }) => (
  <Box
    sx={{
      width: 38,
      height: 38,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      zIndex: 1,
      background: completed
        ? '#2E7D32'
        : active
          ? 'linear-gradient(135deg, #FFF6EA 0%, #FFE7C8 100%)'
          : '#F1F1F7',
      border: active ? '2px solid #F6A03A' : '2px solid transparent',
      color: completed ? '#fff' : active ? '#D9820E' : '#A9AEC0',
      boxShadow: active
        ? '0 6px 16px rgba(246, 160, 58, 0.30)'
        : completed
          ? '0 4px 10px rgba(46, 125, 50, 0.18)'
          : 'none',
      transition: 'all .25s ease',
      '& svg': { fontSize: 19 },
    }}
  >
    {completed ? <CheckRoundedIcon sx={{ fontSize: 19 }} /> : glyph}
  </Box>
);

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    minHeight: '52px',
    borderRadius: '14px',
    border: `1px solid ${C.line}`,
    boxShadow: '0 4px 16px rgba(41, 45, 91, 0.05)',
    paddingLeft: '10px',
    paddingRight: '16px',
    marginLeft: { xs: '12px', sm: '32px' },
    marginRight: { xs: '12px', sm: 0 },
    marginBottom: '20px',
    width: { xs: 'auto', sm: '95%' },
    marginTop: { xs: '16px', sm: '28px' },
  },

  backBtn: {
    padding: 0,
    marginRight: '12px',
    width: '34px',
    height: '34px',
    backgroundColor: '#F4F5F9',
    borderRadius: '50%',
    transition: 'background-color .2s ease, transform .2s ease',
    '&:hover': { backgroundColor: '#EBEDF5', transform: 'translateX(-1px)' },
  },

  backIcon: {
    fontSize: '20px',
    color: C.navy,
  },

  pageHeading: {
    fontSize: '16px',
    fontWeight: 700,
    color: C.navy,
    letterSpacing: '0.1px',
  },
};

const MotionBox = motion(Box);

// ---- Progress hero -----------------------------------------------------
const HERO_LINES = [
  'Let’s map out your future — one step at a time.',
  'Great start. Keep the momentum going!',
  'You’re getting there — nice work.',
  'Over halfway. Your report is taking shape.',
  'Almost done — just the final stretch!',
  'All steps complete. Time to see your results ✨',
];

const ProgressHero = ({ doneCount, total }) => {
  const pct = Math.round((doneCount / total) * 100);
  const line = HERO_LINES[doneCount] || HERO_LINES[0];

  return (
    <Box
      sx={{
        width: { xs: 'auto', sm: '95%' },
        mx: { xs: '12px', sm: '32px' },
        mb: 2.5,
        borderRadius: '20px',
        p: { xs: 2.5, sm: 3.5 },
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
        background:
          'linear-gradient(135deg, #262B61 0%, #363C7C 52%, #4B3E86 100%)',
        boxShadow: '0 18px 42px rgba(38, 43, 97, 0.30)',
      }}
    >
      {/* glow accents */}
      <Box
        sx={{
          position: 'absolute',
          top: -60,
          right: -40,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(246,160,58,0.35) 0%, rgba(246,160,58,0) 70%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -80,
          left: -30,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(120,200,205,0.22) 0%, rgba(120,200,205,0) 70%)',
        }}
      />

      <Box sx={{ position: 'relative', display: 'flex', alignItems: { sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.65)',
              mb: 0.5,
            }}
          >
            Career Assessment
          </Typography>
          <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 800, lineHeight: 1.2 }}>
            {doneCount === 0
              ? 'Discover the career that fits you'
              : doneCount === total
                ? 'You’ve finished every step'
                : `You’re ${pct}% of the way there`}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', mt: 0.75 }}>
            {line}
          </Typography>

          {/* progress bar */}
          <Box sx={{ mt: 2, height: 9, borderRadius: 99, backgroundColor: 'rgba(255,255,255,0.16)', overflow: 'hidden' }}>
            <MotionBox
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              sx={{
                height: '100%',
                borderRadius: 99,
                background: 'linear-gradient(90deg, #F6A03A 0%, #FFC873 100%)',
                boxShadow: '0 0 14px rgba(246,160,58,0.6)',
              }}
            />
          </Box>
        </Box>

        {/* step count chip */}
        <Box
          sx={{
            flexShrink: 0,
            textAlign: 'center',
            px: 2.5,
            py: 1.5,
            borderRadius: '16px',
            backgroundColor: 'rgba(255,255,255,0.10)',
            border: '1px solid rgba(255,255,255,0.16)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}>
            {doneCount}
            <Box component="span" sx={{ fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>
              {' '}/ {total}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 10.5, letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
            steps done
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const infoCardSx = {
  width: '100%',
  maxWidth: 620,
  backgroundColor: '#FFFFFF',
  borderRadius: '20px',
  p: { xs: 3, sm: 4 },
  boxShadow: '0 16px 40px rgba(41, 45, 91, 0.08)',
  border: `1px solid ${C.line}`,
  textAlign: 'center',
};

const primaryBtnSx = {
  backgroundColor: C.orange,
  borderRadius: '12px',
  px: 4,
  py: 1.1,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: 15,
  boxShadow: '0 10px 22px rgba(246, 160, 58, 0.32)',
  transition: 'transform .18s ease, box-shadow .18s ease, background-color .18s ease',
  '&:hover': {
    backgroundColor: C.orangeDark,
    boxShadow: '0 12px 26px rgba(246, 160, 58, 0.4)',
    transform: 'translateY(-1px)',
  },
};

const ReportReadyCard = ({ onGenerate, onReset }) => (
  <MotionBox
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    sx={infoCardSx}
  >
    <Box
      sx={{
        width: 60,
        height: 60,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #E7F6E9 0%, #D2EFD6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 2,
      }}
    >
      <CheckCircleRoundedIcon sx={{ color: C.green, fontSize: 34 }} />
    </Box>
    <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.navy, mb: 1 }}>
      Your assessment is complete 🎉
    </Typography>
    <Typography sx={{ fontSize: 13.5, color: '#697089', mb: 3, lineHeight: 1.7, maxWidth: 460, mx: 'auto' }}>
      Parent/Teacher feedback is in. Generate your career report whenever you’re ready —
      keep this page open while it prepares and saves to your Reports section.
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
      <Button variant="contained" onClick={onGenerate} startIcon={<AutoAwesomeRoundedIcon />} sx={primaryBtnSx}>
        Generate Report
      </Button>
      <Button
        variant="text"
        onClick={onReset}
        startIcon={<RestartAltRoundedIcon />}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 14,
          color: C.muted,
          borderRadius: '12px',
          px: 2,
          '&:hover': { backgroundColor: '#F1F2F6', color: C.navy },
        }}
      >
        Reset test
      </Button>
    </Box>
  </MotionBox>
);

const ContinueAssessmentCard = ({ onContinue }) => (
  <MotionBox
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    sx={infoCardSx}
  >
    <Box
      sx={{
        width: 60,
        height: 60,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #FFF3E1 0%, #FFE4BF 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 2,
      }}
    >
      <PlayArrowRoundedIcon sx={{ color: C.orange, fontSize: 36 }} />
    </Box>
    <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.navy, mb: 1 }}>
      Pick up where you left off
    </Typography>
    <Typography sx={{ fontSize: 13.5, color: '#697089', mb: 3, lineHeight: 1.7, maxWidth: 460, mx: 'auto' }}>
      A few required sections are still pending. Finish them to unlock your career report.
    </Typography>
    <Button variant="contained" onClick={onContinue} sx={primaryBtnSx}>
      Continue Assessment
    </Button>
  </MotionBox>
);

const Assessment = () => {
  const [steps, setSteps] = useState(createInitialSteps);
  const [activeStep, setActiveStep] = useState(null);
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [reportGeneratorStarted, setReportGeneratorStarted] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollContainerRef = useRef(null);
  const stepRefs = useRef([]);

  const allIncomplete = steps.every(step => !step.is_Completed);
  const allStepsComplete = steps.every(step => step.is_Completed);
  const firstIncompleteStep = steps.findIndex(step => !step.is_Completed);
  const doneCount = steps.filter(step => step.is_Completed).length;
  // Display-only: which node the track highlights. Falls back to the first
  // pending step so the track never looks fully locked. Does not touch state.
  const displayActiveStep =
    activeStep !== null
      ? activeStep
      : allStepsComplete
        ? steps.length
        : firstIncompleteStep === -1
          ? 0
          : firstIncompleteStep;
  const visibleSteps = steps.map((step, index) => ({
    ...step,
    is_Completed:
      firstIncompleteStep === -1 || index < firstIncompleteStep
        ? step.is_Completed
        : false,
  }));

  // Scroll the active (or first incomplete) step into view inside the
  // horizontal Stepper container on every step change. Active beats incomplete.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const targetIndex =
      activeStep !== null && activeStep >= 0
        ? activeStep
        : steps.findIndex((s) => !s.is_Completed);
    if (targetIndex < 0) return;
    const target = stepRefs.current[targetIndex];
    if (!target) return;
    // Center the active step inside the scroll viewport without scrolling the page.
    const offset =
      target.offsetLeft - container.clientWidth / 2 + target.clientWidth / 2;
    container.scrollTo({ left: Math.max(0, offset), behavior: 'smooth' });
  }, [activeStep, steps]);

  // const getActiveFromBackend = () =>
  //   steps.findIndex(step => !step.is_Completed);

  // useEffect(() => {
  //   if (!allIncomplete) {
  //     setActiveStep(getActiveFromBackend());
  //   }
  // }, [allIncomplete, steps]);

  const handleStart = () => {
    setActiveStep(0);
  };
  const handleStepComplete = () => {
    const updatedSteps = steps.map((step, index) => (
      index === activeStep ? { ...step, is_Completed: true } : step
    ));

    setSteps(updatedSteps);
    const isAssessmentComplete = updatedSteps.every(s => s.is_Completed);

    if (isAssessmentComplete) {
      setActiveStep(null);
      setReportGeneratorStarted(false);
      return;
    }

    const nextStep = updatedSteps.findIndex(s => !s.is_Completed);
    setActiveStep(nextStep === -1 ? null : nextStep);
  };
  const fetchProgressStatus = async () => {
    let data = {
      marks_uploaded: false,
      parent_feedback_taken: false,
      general_personality_question: false,
      aptitude_test: false,
      self_introspection: false
    };

    try {
      const response = await httpcommon.get(
        "school/student/progress-status/"
      );

      if (response.status === 200) {
        data = response.data || data;
      }
    } catch (error) {
      console.error("Failed to fetch progress status", error);
    }

    const updatedSteps = initialSteps.map(step => ({
      ...step,
      is_Completed: !!data?.[step.id],
    }));

    setSteps(updatedSteps);
    setReportGeneratorStarted(false);

    const firstIncomplete = updatedSteps.findIndex(
      step => !step.is_Completed
    );
    const hasCompletedStep = updatedSteps.some(
      step => step.is_Completed
    );
    console.log("First incomplete step index:", firstIncomplete);
    setActiveStep(firstIncomplete === -1 ? null : hasCompletedStep ? firstIncomplete : null);
  };
  const handleResetAssessment = async () => {
    if (resetting) return;
    try {
      setResetting(true);
      await httpcommon.post("school/student/reset-assessment/");
      // The reset endpoint has completed successfully.  Do not re-read stale
      // progress before returning the student to the first-screen briefing.
      setReportGeneratorStarted(false);
      setSteps(createInitialSteps());
      setActiveStep(null);
      setOpenResetDialog(false);
    } catch (error) {
      console.error("Failed to reset assessment", error);
      return;
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {

    fetchProgressStatus();
  }, []);


  return (
    <LoggedInLayout searchBar={false}>
      <Box sx={{ backgroundColor: C.bg, minHeight: '100%', pb: 4 }}>
        <Box sx={styles.header}>
          <IconButton onClick={() => {
            if (window.__assessmentBusy) {
              const proceed = window.confirm(
                "Your report is still being prepared. Leaving now will cancel the process. Continue?"
              );
              if (!proceed) return;
            }
            navigate("/");
          }} sx={styles.backBtn}>
            <ArrowBack sx={styles.backIcon} />
          </IconButton>

          <Typography sx={styles.pageHeading}>
            Career Assessment Test
          </Typography>
        </Box>

        <ProgressHero doneCount={doneCount} total={steps.length} />

        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              mx: { xs: '12px', sm: '32px' },
            }}
          >
            <Box
              ref={scrollContainerRef}
              sx={{
                flex: 1,
                minWidth: 0,
                backgroundColor: '#FFFFFF',
                border: `1px solid ${C.line}`,
                borderRadius: '16px',
                boxShadow: '0 6px 22px rgba(41, 45, 91, 0.05)',
                overflowX: { xs: 'auto', sm: 'hidden' },
                scrollBehavior: 'smooth',
                pt: 2.25,
                pb: 1.75,
                px: { xs: 1.5, sm: 2.5 },
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(41,45,91,0.15)', borderRadius: 3 },
              }}
            >
              <Stepper
              activeStep={displayActiveStep}
              alternativeLabel
              orientation="horizontal"
              connector={<Connector />}
              sx={{
                minWidth: { xs: 'max-content', sm: 'auto' },
              }}
            >
              {visibleSteps.map((step, index) => {
                const isActive = index === displayActiveStep;
                const isCompleted = step.is_Completed;

                return (
                  <Step
                    key={step.id}
                    completed={isCompleted}
                    ref={(el) => { stepRefs.current[index] = el; }}
                  >
                    <StepLabel
                      StepIconComponent={(iconProps) => (
                        <CompactStepIcon
                          active={iconProps.active}
                          completed={iconProps.completed}
                          glyph={step.icon}
                        />
                      )}
                      sx={{
                        '& .MuiStepLabel-label': {
                          mt: '7px !important',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          letterSpacing: '0.1px',
                          color: isCompleted ? C.green : isActive ? C.navy : '#9AA0B2',
                        },
                      }}
                    >
                      {step.short}
                    </StepLabel>
                  </Step>
                );
              })}
              </Stepper>
            </Box>

            <Button
              onClick={() => setOpenResetDialog(true)}
              disabled={resetting}
              startIcon={<RestartAltRoundedIcon sx={{ fontSize: 17 }} />}
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 12.5,
                color: C.muted,
                backgroundColor: '#FFFFFF',
                border: `1px solid ${C.line}`,
                borderRadius: '12px',
                px: { xs: 1.25, sm: 1.75 },
                py: 1,
                boxShadow: '0 6px 22px rgba(41, 45, 91, 0.05)',
                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } },
                '&:hover': { backgroundColor: '#F7F8FB', color: C.navy, borderColor: '#CFD4E2' },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Reset test</Box>
            </Button>
          </Box>

          <Box
            mt={{ xs: 2.5, sm: 3 }}
            px={{ xs: 1.5, sm: 4 }}
            pt={2}
            pb={{ xs: 6, sm: 4 }}
            margin="0 auto"
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{ width: '100%', maxWidth: 1000, boxSizing: 'border-box' }}
          >
            {/* GUIDELINES */}
            {allIncomplete && activeStep === null && (
              <MotionBox
                key="guidelines"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
              >
                <AssessmentGuidelines onStart={handleStart} />
              </MotionBox>
            )}

            {/* ACTIVE STEP CONTENT */}
            {activeStep !== null && !allStepsComplete && (
              <MotionBox
                key={`step-${activeStep}`}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                sx={{ width: '100%' }}
              >
                <AssessmentStepRender
                  stepIndex={activeStep}
                  onComplete={handleStepComplete}
                />
              </MotionBox>
            )}

            {activeStep === null && !allIncomplete && !allStepsComplete && (
              <MotionBox
                key="continue"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
              >
                <ContinueAssessmentCard
                  onContinue={() => setActiveStep(firstIncompleteStep === -1 ? 0 : firstIncompleteStep)}
                />
              </MotionBox>
            )}

            {/* THANK YOU SCREEN */}
            {allStepsComplete && reportGeneratorStarted && (
              <Box sx={{ width: '100%' }}>
                <AssessmentPdf></AssessmentPdf>
              </Box>
            )}

            {allStepsComplete && !reportGeneratorStarted && (
              <MotionBox
                key="ready"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
              >
                <ReportReadyCard
                  onGenerate={() => setReportGeneratorStarted(true)}
                  onReset={() => setOpenResetDialog(true)}
                />
              </MotionBox>
            )}
          </Box>
        </Box>
      </Box>
      <Dialog
        open={openProfileDialog}
        onClose={() => setOpenProfileDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: C.navy }}>
          Complete Your Profile
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ color: C.ink, lineHeight: 1.7 }}>
            Your profile is incomplete. Please add your board and class to continue with the assessment.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenProfileDialog(false)}
            sx={{ textTransform: 'none', color: C.muted, fontWeight: 600 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setOpenProfileDialog(false);
              navigate("/profile");
            }}
            sx={primaryBtnSx}
          >
            Go to Profile
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openResetDialog}
        onClose={() => !resetting && setOpenResetDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: C.navy }}>
          Reset the whole assessment?
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ color: C.ink, lineHeight: 1.7 }}>
            This clears your marks, "Help Us Know You" answers, the aptitude and
            self-introspection tests, and the parent/teacher feedback. You'll
            start again from the Before you start briefing. Reports you've already
            generated are kept. This can't be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenResetDialog(false)}
            disabled={resetting}
            sx={{ textTransform: 'none', color: C.muted, fontWeight: 600 }}
          >
            Keep my answers
          </Button>

          <Button
            variant="contained"
            onClick={handleResetAssessment}
            disabled={resetting}
            startIcon={<RestartAltRoundedIcon />}
            sx={primaryBtnSx}
          >
            {resetting ? 'Resetting…' : 'Yes, reset test'}
          </Button>
        </DialogActions>
      </Dialog>
    </LoggedInLayout>
  );
};

export default Assessment;
